import { Injector } from "@angular/core";
import esri = __esri;
import { loadModules } from "esri-loader";
import { Observable, from, empty, of, combineLatest } from "rxjs";
import { concatMap, map, tap } from "rxjs/operators";

import { Renderer, CONFIG, MAP_ELEMENT } from "./renderer.interface";
import { AccessPlan } from "../../../models/access-plan.model";
import { VIEW_WIDGETS } from "../models/widgets.model";
import { GISConfig } from "../../../models/gis-config.model";
import { Point } from "../../../models/point.model";
import { formattedRouteString } from "../../../models/formatted-route-string.model";

export class RouteMapRenderer2D extends Renderer<esri.MapView> {
    protected map: esri.Map;
    _plan: AccessPlan;

    accessToken: string;
    allowClick: boolean = true;
    baseMap: string = "streets";

    defaultConfig()  {
        return {
            properties: {
                basemap: this.baseMap,
                zoom: 18,
                center: [-82.965020, 40.059260] //ODNR HQ
            } as esri.MapProperties,
            widgets: []
        }
    }

    gisConfig: GISConfig;
    ingressMode: boolean = true;

    pointLayer: esri.GraphicsLayer;
    routeLayer: esri.GraphicsLayer;
    routeParams: esri.RouteParameters;
    routeResult: esri.Graphic;
    routeStrings: formattedRouteString[] = [];
    routeSymbol: any;
    routeTask: esri.RouteTask;
    stopSymbol: any;
    wellPoint: esri.Graphic;

    constructor(plan: AccessPlan, injector: Injector, enableClick: boolean) {
        super(injector);
        this._plan = plan ?? new AccessPlan([], true);
        this.config = Object.assign(this.defaultConfig(), injector.get(CONFIG, this.defaultConfig()));
        this.config.properties = Object.assign(this.defaultConfig().properties, this.config.properties);
        this.containerElement = injector.get(MAP_ELEMENT);
        this.gisConfig = window["GIS.Conf"] as GISConfig;
        this.allowClick = enableClick;
    }


    render(): Observable<any> {
        return this.generateLayers().pipe(concatMap((layers) => {
            this.map.addMany(layers);
            let obs$: Observable<any>;
            if (this._plan && this._plan.points && this._plan.points.length > 0) {
                let points = [];
                this._plan.points.forEach(x => points.push(from(this._addPoint(x))));
                obs$ = combineLatest(points).pipe(tap((result) => {
                    return from(this._performRouting());
                }));
            } else {
                obs$ = of([]);
            }
            return obs$;
        }));
    }

    initialize(): Observable<esri.MapView> {
        this.config = Object.assign(this.defaultConfig(), this.injector.get(CONFIG, this.defaultConfig()));
        this.config.properties = Object.assign(this.defaultConfig().properties, this.config.properties);

        return from(loadModules([
            "esri/config",
            "esri/Map",
            "esri/views/MapView",
            "esri/tasks/support/RouteParameters",
            "esri/tasks/support/FeatureSet", 
            "esri/tasks/RouteTask"
        ])).pipe(concatMap(([esriConfig, EsriMap, MapView, RouteParameters, FeatureSet, RouteTask]) => {            
            esriConfig.apiKey = this.gisConfig.routingServicesApiKey;
           
            this.map = new EsriMap(this.config.properties);
            let mapProps = {
                container: this.containerElement,
                center: this.config.properties.center,
                zoom: this.config.properties.zoom,
                map: this.map,
                constraints: {
                    snapToZoom: false,
                    rotationEnabled: false
                }
            };
            this._view = new MapView(mapProps);
            if (this.allowClick) { 
                this._view.on("click", (event) => {
                    this._view.hitTest(event).then(response => {
                        if (response.results.length) {
                            let graphic = response.results[0].graphic;
                            if (graphic && graphic !== this.wellPoint && graphic.geometry.type !== "polyline") {
                                let pointIdx = this._plan.points.findIndex(x => x.id === (graphic as any).gfxId);
                                if (pointIdx !== -1) {
                                    this._plan.points.splice(pointIdx, 1);
                                }
                                let stops = <esri.FeatureSet>this.routeParams.stops;
                                pointIdx = stops.features.findIndex(x => (x as any).uid === (graphic as any).uid);
                                if (pointIdx !== -1) {
                                    stops.features.splice(pointIdx, 1);
                                }
                                this.pointLayer.remove(graphic);
                                if (stops.features.length > 1) {
                                    this._performRouting().then(() => { });
                                } else {
                                    this.routeLayer.remove(this.routeResult);
                                    this.routeStrings = [];
                                    this._plan = new AccessPlan(this.routeStrings, this.ingressMode, this._plan.points);
                                    this.dataUpdated.next(this._plan);
                                }

                            }
                        } else {
                            this._addStop(event).then(() => { });
                        }
                    });
                });
            }
            

            this.stopSymbol = {
                type: "simple-marker",
                color: [226, 119, 226],
                outline: {
                    color: [0, 0, 0],
                    width: 2
                }
            };
            this.routeSymbol = {
                type: "simple-line",
                color: [0, 0, 255, 0.5],
                width: 5
            };

            this.routeParams = new RouteParameters({
                stops: new FeatureSet(),
                returnRoutes: true,
                returnDirections: true,
                directionsOutputType: "complete-no-events",
                returnStops: true,
                outSpatialReference: { wkid: 3857 }
            });

            this.routeTask = new RouteTask({
                url: `${this.gisConfig.routingServicesUrl}?token=${this.gisConfig.routingServicesApiKey}`
            });
            return this.createWidgets().pipe(map(() => this._view));
        }));
    }

    protected createWidgets(): Observable<any> {
        if (this.config.widgets && this._view) {
            return from(loadModules([
                "esri/widgets/BasemapToggle",
                "esri/widgets/LayerList",
                "esri/widgets/Search",
                "esri/widgets/Compass"
            ])).pipe(map(([BasemapToggle, LayerList, Search, Compass]) => {
                this.config.widgets.forEach(x => {
                    let widget = null;
                    switch (x.type) {
                        case VIEW_WIDGETS.basemapToggle:
                            widget = new BasemapToggle({
                                view: this._view
                            });
                            break;
                        case VIEW_WIDGETS.compass:
                            widget = new Compass({
                                view: this._view
                            });
                            break;
                        case VIEW_WIDGETS.layerList:
                            widget = new LayerList({
                                view: this._view,
                                listItemCreatedFunction: function (event) {
                                    setTimeout(function () {
                                        const item = event.item;
                                        const itemUId = `${widget.id}_${item.uid}__title`;
                                        const node = document.getElementById(itemUId);
                                        if (node) {
                                            node.style.color = `rgb(${item.layer.graphics.items[0].symbol.color.r}, ${item.layer.graphics.items[0].symbol.color.g}, ${item.layer.graphics.items[0].symbol.color.b})`; //done to eliminate any opacity dimension from the existing color
                                            node.style.fontSize = "8pt";
                                            if (node.parentElement) {
                                                const parent = node.parentElement;
                                                if (parent.parentElement) {
                                                    const grandparent = parent.parentElement;
                                                    grandparent.style.padding = "0px";
                                                }
                                            }
                                        }
                                    }, 50);
                                }
                            });
                            break;
                        default:
                            console.debug("widget type not supported.");
                            break;
                    }
                    this._view.ui.add(widget, x.position);
                });
            }));
        } else {
            return empty();
        }
    }

    private generateLayers(): Observable<any> {
        return from(loadModules([
            "esri/layers/GraphicsLayer",
            "esri/Graphic"
        ])).pipe(concatMap(([GraphicsLayer, Graphic]) => {
            this.routeLayer = new GraphicsLayer();
            this.pointLayer = new GraphicsLayer();

            const centerSymbol = {
                type: "simple-marker",
                color: [226, 119, 40],
                outline: {
                    color: [255, 255, 255],
                    width: 2
                }
            };
            const point = {
                type: "point",
                longitude: this.config.properties.center[0],
                latitude: this.config.properties.center[1]
            };

            const centerPoint = new Graphic({
                geometry: point,
                symbol: centerSymbol
            });
            this.pointLayer.add(centerPoint);
            let stops: esri.FeatureSet = <esri.FeatureSet>this.routeParams.stops;
            stops.features.push(centerPoint);
            this.wellPoint = centerPoint;
            return of([this.routeLayer, this.pointLayer]);
        }));
    }

    export(): Observable<any> {
        return from(this.view.takeScreenshot());
    }



    private async _addPoint(point: Point) {
        const [Graphic, Point] = await loadModules([
            "esri/Graphic",
            "esri/geometry/Point"
        ]);

        const stop = new Graphic({
            geometry: new Point(point.longitude, point.latitude),
            symbol: this.stopSymbol
        });

        let sym = Symbol('identifier');
        stop.gfxId = sym;
        point.id = sym;
        this.pointLayer.add(stop);
        let stops: esri.FeatureSet = <esri.FeatureSet>this.routeParams.stops;
        stops.features.unshift(stop);
    }

    private async _addStop(event) {
        const sym = Symbol("identifier");
        
        let point = {
            longitude: event.mapPoint.longitude,
            latitude: event.mapPoint.latitude,
            id: sym
        } as Point;

        await this._addPoint(point);
        if (!this._plan.points) {
            this._plan.points = [];
        }
        this._plan.points.unshift(point);
        await this._performRouting();
    }

    private async _performRouting() {
        this.routeStrings = [];
        let stops = <esri.FeatureSet>this.routeParams.stops;
        if (stops.features.length < 2) {
            this._plan = new AccessPlan(this.routeStrings, this.ingressMode, this._plan.points);
            this.dataUpdated.next(this._plan);
        } else {
            return this.routeTask.solve(this.routeParams).then(result => {
                let data = result as any; //have to cast to any because the live data returned doesn't match to the type models
                this.showRoute(data);
                
                if (data && data.routeResults.length && data.routeResults[0].directions) {                   
                    if (data.routeResults[0].directions.strings && Array.isArray(data.routeResults[0].directions.strings)) {
                        for (let i = 0; i < data.routeResults[0].directions.strings.length; i++) {
                            const directionObj = data.routeResults[0].directions.strings[i];
                            if (directionObj != null) {
                                let tempFormatted: formattedRouteString = new formattedRouteString();

                                for (let j = 0; j < directionObj.length; j++) {
                                    switch (directionObj[j].stringType) {
                                        case "esriDSTStreetName":
                                            tempFormatted.streetName = directionObj[j].string;
                                            break;
                                        case "esriDSTCrossStreet":
                                            tempFormatted.crossStreet = directionObj[j].string;
                                            break;
                                    }
                                }
                                this.routeStrings.push(tempFormatted);
                            }
                            
                        }
                    }
                    this._plan = new AccessPlan(this.routeStrings, this.ingressMode, this._plan.points);
                    this.dataUpdated.next(this._plan);
                    if (this.ingressMode) {
                        this.routeStrings = this.routeStrings.reverse();
                    }
                }
            }, (error) => {
                console.error(error);
            });
        }
        
    }

    private showRoute(data: any): void {
        this.routeLayer.remove(this.routeResult);
        this.routeResult = data.routeResults[0].route;
        this.routeResult.symbol = this.routeSymbol;
        this.routeLayer.add(this.routeResult);
    }

}